/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getUser,
  patchUser,
  type User,
  activateGodMode,
  deactivateGodMode,
} from "../services/UserService.ts";
import {
  getTokensFromStorage,
  subscribeToAuthTokenChanges,
} from "../services/TokenService.ts";
import { identify, identifyReddit } from "../utils/analytics";
import { identifyLogfrogUser, resetLogfrog } from "../utils/logfrog";
import { reportAcquisition } from "../services/LifecycleService";
import { reportEmailTouch } from "../services/EmailTouchService";

interface UserContextType {
  user: User | null;
  load: () => void;
  isLoading: boolean;
  isGodMode: boolean;
  isFreePlanPreview: boolean;
  godModeTargetUser: User | null;
  activateGodMode: (targetUserId: string) => Promise<void>;
  deactivateGodMode: () => Promise<void>;
  activateFreePlanPreview: () => void;
  deactivateFreePlanPreview: () => void;
  updateUser: (userData: Partial<User>) => Promise<User>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  load: () => {},
  isLoading: false,
  isGodMode: false,
  isFreePlanPreview: false,
  godModeTargetUser: null,
  activateGodMode: async () => {},
  deactivateGodMode: async () => {},
  activateFreePlanPreview: () => {},
  deactivateFreePlanPreview: () => {},
  updateUser: async () => ({} as User),
});

const FREE_PLAN_PREVIEW_KEY = "dealhawk_admin_free_plan_preview";

const getStoredPreviewOwner = () => {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(FREE_PLAN_PREVIEW_KEY);
};

export function useUserContext() {
  return useContext(UserContext);
}

const getAuthSessionSnapshot = () => !!getTokensFromStorage();
const getServerAuthSessionSnapshot = () => false;

export function UserProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [freePlanPreviewOwner, setFreePlanPreviewOwner] = useState<string | null>(getStoredPreviewOwner);
  const hasAuthToken = useSyncExternalStore(
    subscribeToAuthTokenChanges,
    getAuthSessionSnapshot,
    getServerAuthSessionSnapshot
  );

  const {
    data: queriedUser,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["user"],
    queryFn: () => getUser(),
    enabled: hasAuthToken,
    retry: (failureCount, error: unknown) => {
      // Don't retry if it's an authentication error (401/403)
      const errorStatus =
        error && typeof error === "object" && "status" in error
          ? (error as { status: number }).status
          : undefined;
      if (errorStatus === 401 || errorStatus === 403) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (!hasAuthToken) {
      queryClient.removeQueries({ queryKey: ["user"], exact: true });
    }
  }, [hasAuthToken, queryClient]);

  const authenticatedUser = hasAuthToken ? queriedUser : undefined;
  const isGodMode = authenticatedUser?.godModeUserId ? true : false;
  const isFreePlanPreview = !!(
    authenticatedUser?.admin &&
    !isGodMode &&
    freePlanPreviewOwner?.toLowerCase() === authenticatedUser.userId.toLowerCase()
  );

  // This is deliberately a client-side entitlement preview. It lets an admin
  // exercise the Free UI with their own preferences without changing the
  // account row, subscription, or retailer grants in production.
  const user = authenticatedUser && isFreePlanPreview
    ? {
        ...authenticatedUser,
        accountType: "free" as const,
        retailers: [],
        admin: false,
        allowAddLead: false,
        allowSendToDiscord: false,
        allowMoreStores: false,
        maxAlertsOverride: undefined,
        retailerAccessOverrides: {},
        freeTierEnabled: true,
        setUp: true,
        tourSeen: true,
        dealIntent: authenticatedUser.dealIntent || "online" as const,
      }
    : authenticatedUser;

  useEffect(() => {
    if (authenticatedUser) {
      identify(authenticatedUser.userId, {
        name: authenticatedUser.userId,
        email: authenticatedUser.userId,
        created: String(authenticatedUser.created || ""),
        has_home_depot: authenticatedUser.retailers?.find((r) => r.includes("home_depot")) ? "true" : "false",
        has_walmart: authenticatedUser.retailers?.find((r) => r.includes("walmart")) ? "true" : "false",
        stores: String(authenticatedUser.storeIds?.length || 0),
        hd_stores: String(authenticatedUser.homeDepotStoreIds?.length || 0),
        categories: String(authenticatedUser.categories?.length || 0),
        hd_categories: String(authenticatedUser.homeDepotCategories?.length || 0),
      });
      identifyReddit({ email: authenticatedUser.userId, externalId: authenticatedUser.userId });
      identifyLogfrogUser(authenticatedUser);
      // Every sign-in path (email, Google, Discord, Apple, restored session)
      // lands here. An admin impersonating an account must not stamp this
      // browser's first touch onto it.
      if (!authenticatedUser.godModeUserId) {
        reportAcquisition(authenticatedUser.userId);
        reportEmailTouch();
      }
    } else if (!hasAuthToken) {
      resetLogfrog();
    }
  }, [authenticatedUser, hasAuthToken]);

  if (isLoading) {
    return null;
  }

  // God mode state calculations
  const godModeTargetUser = isGodMode ? authenticatedUser || null : null;

  const handleActivateFreePlanPreview = () => {
    if (!authenticatedUser?.admin || isGodMode) return;
    window.sessionStorage.setItem(FREE_PLAN_PREVIEW_KEY, authenticatedUser.userId);
    setFreePlanPreviewOwner(authenticatedUser.userId);
  };

  const handleDeactivateFreePlanPreview = () => {
    window.sessionStorage.removeItem(FREE_PLAN_PREVIEW_KEY);
    setFreePlanPreviewOwner(null);
  };

  const handleActivateGodMode = async (targetUserId: string) => {
    try {
      const updatedUser = await activateGodMode(targetUserId);
      queryClient.setQueryData(["user"], updatedUser);
      // Clear last selected store so the target user's preferences can be loaded
      localStorage.removeItem("dealhawk_last_selected_store");
    } catch (error) {
      console.error("Failed to activate god mode:", error);
      throw error;
    }
  };

  const handleDeactivateGodMode = async () => {
    try {
      const updatedUser = await deactivateGodMode();
      queryClient.setQueryData(["user"], updatedUser);
      // Clear last selected store so the admin's preferences can be loaded
      localStorage.removeItem("dealhawk_last_selected_store");
    } catch (error) {
      console.error("Failed to deactivate god mode:", error);
      throw error;
    }
  };

  const handleUpdateUser = async (userData: Partial<User>) => {
    try {
      const updatedUser = await patchUser(userData);
      queryClient.setQueryData<User>(["user"], (old) =>
        old ? { ...old, ...updatedUser } : updatedUser
      );
      return queryClient.getQueryData<User>(["user"]) ?? updatedUser;
    } catch (error) {
      console.error("Failed to update user:", error);
      throw error;
    }
  };

  return (
    <UserContext.Provider
      value={{
        user: user || null,
        load: refetch,
        isLoading,
        isGodMode,
        isFreePlanPreview,
        godModeTargetUser,
        activateGodMode: handleActivateGodMode,
        deactivateGodMode: handleDeactivateGodMode,
        activateFreePlanPreview: handleActivateFreePlanPreview,
        deactivateFreePlanPreview: handleDeactivateFreePlanPreview,
        updateUser: handleUpdateUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
